import AgyleDialog from "../../modal/agyleDialog";
import useOpenState from "../../../hooks/useOpenState";
import MessageBox from "../../../util/messageBox";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {announcements, deleteAnnouncement, updateAnnouncement} from "../../../store/reducers/announcement";
import Grid from "@mui/material/Grid";
import React, {Fragment, useEffect, useRef, useState} from 'react';
import {hasPermission} from "../../../store/reducers/user";
import {Divider} from '@mui/material';
import moment from "moment/moment";
import makeStyles from "@mui/styles/makeStyles";
import _ from "lodash";
import AgyleTextField from '../../ui/fields/agyleTextField';
import AgyleButton from '../../ui/buttons/agyleButton';
import AgyleSwitch from '../../ui/switch/agyleSwitch';
import FormLabel from '@mui/material/FormLabel';
import AgyleDatePicker from '../../ui/fields/agyleDatePicker';
import {getConfig} from '../../../util/configUtil';

const useStyles = makeStyles((theme) => ({
    paper: {
        margin: "25px 10px 10px 10px",
        borderRadius: "7px",
        background: theme.palette.background.paper,
        padding: "0px 10px 10px 10px",
    },
    input: {
        width: "100%",
        fontSize: 15,
        marginBottom: 10,
    },
    button: {
        marginTop: 15,
        width: "100%",
    },
    announcement: {
        padding: 10,
        backgroundColor: theme.palette.background.paper,
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    }
}));

const AnnouncementPanel = ({open, onClose}) => {
    const {t} = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();

    // Announcement state
    const announcementsObject = useSelector(announcements);
    const [announcementValues, setAnnouncementValues] = useState([]);
    const [announcementText, setAnnouncementText] = useState("");
    const deleteID = useRef(null);

    // Open states
    const [isAddAnnouncementOpen, openAddAnnouncement, closeAddAnnouncement] = useOpenState(false, true);
    const [isDeleteAnnouncementOpen, openDeleteAnnouncement, closeDeleteAnnouncement] = useOpenState(false, true);

    useEffect(() => {
        setAnnouncementValues(announcementsObject);
    }, [announcementsObject]);

    const handleChangeNewAnnouncement = (event) => {
        const {value} = event.target;
        setAnnouncementText(value);
    };

    const handleCloseDeleteAnnouncements = (e, reason) => {
        deleteID.current = null;
        closeDeleteAnnouncement(e, reason);
    };

    const handleDeleteAnnouncement = () => {
        closeDeleteAnnouncement();
        if (deleteID.current && dispatch(hasPermission("CAN_ADD_ANNOUNCEMENT"))) {
            dispatch(deleteAnnouncement(deleteID.current));
            deleteID.current = null;
        }
    };

    const handleAddAnnouncement = () => {
        if (announcementText !== "" && dispatch(hasPermission("CAN_ADD_ANNOUNCEMENT"))) {
            const obj = {
                "id": null,
                "enabled": true,
                "text": announcementText,
            };
            dispatch(updateAnnouncement(obj));
            closeAddAnnouncement();
            setAnnouncementText("");
        }
    };

    const getSystem = (announcement, system) => {
        let newType;
        if (system === "AGYLE") {
            if (announcement.system === "UCR") newType = "BOTH";
            if (announcement.system === "BOTH") newType = "UCR";
            if (announcement.system === system) newType = "UCR";
            if (announcement.system === "ED WAIT") newType = "AGYLE"
        }
        if (system === "UCR") {
            if (announcement.system === "AGYLE") newType = "BOTH";
            if (announcement.system === "BOTH") newType = "AGYLE";
            if (announcement.system === system) newType = "AGYLE";
            if (announcement.system === "ED WAIT") newType = "UCR"
        }
        if (system === 'ED WAIT') newType = 'ED WAIT'

        return newType;
    };

    const handleChangeAnnouncement = (event, system) => {
        const {name, id} = event.target;
        const value = event.target[name];
        const newArray = _.cloneDeep(announcementValues);

        // Find index and update the changed item.
        let index = _.findIndex(newArray, {"id": id});
        if (index !== -1) {
            let obj = {...newArray[index]};
            // eslint-disable-next-line default-case
            switch (name) {
                case "value":
                    obj = {...obj, text: value};
                    break;
                case "checked":
                    obj = {...obj, enabled: value};
                    break;
                case "counter":
                    obj = {...obj, counter: event.target.checked};
                    break;
                case "system":
                    obj = {...obj, system: getSystem(obj, system)};
                    break;
                case "countdownTo":
                    obj = {...obj, countdownTo: !obj.counter ? null : event.target.value};
                    break;
            }
            newArray[index] = obj;
            setAnnouncementValues(newArray);
        }
    };

    const handleChangeDateAnnouncement = (date, id) => {
        handleChangeAnnouncement({target: {name: "countdownTo", value: date, id}});
    };

    const handleSaveAnnouncements = (element) => {
        if (element.text !== "")
            dispatch(updateAnnouncement(element));
    };

    return (
        <>
            <AgyleDialog
                title={t("Announcements")}
                open={open}
                onClose={onClose}
                width="lg"
                fullWidth
                onSave={openAddAnnouncement}
                submitText="Add"
            >
                {announcementValues?.map((element, index) => (
                    <Fragment key={element.id}>
                        {index !== 0 && <Divider style={{margin: '10px 0'}}/>}

                        <Grid
                            container
                            spacing={1}
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Grid item xs={12}>
                                <AgyleTextField
                                    id={element.id}
                                    name={"value"}
                                    label={t("Text")}
                                    onChange={handleChangeAnnouncement}
                                    value={element.text}
                                    multiline
                                    debounce
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12}>
                                {element.counter &&
                                    <AgyleDatePicker
                                        label={t("Date and Time")}
                                        value={element.countdownTo}
                                        minDate={moment()}
                                        onChange={(value) => handleChangeDateAnnouncement(value, element.id)}
                                        type="dateTime"
                                        disableFuture={false}
                                        disablePast={false}
                                    />
                                }
                            </Grid>

                            <Grid item xs={12}>
                                <AgyleSwitch
                                    value={element.enabled}
                                    name={"checked"}
                                    id={element.id}
                                    onChange={handleChangeAnnouncement}
                                    label={t('Enabled')}
                                />

                                <AgyleSwitch
                                    value={element.system !== "AGYLE" && element.system !== 'ED WAIT'}
                                    id={element.id}
                                    onChange={(e) => handleChangeAnnouncement(e, "UCR")}
                                    name={"system"}
                                    label={t('For UCR')}
                                />

                                <AgyleSwitch
                                    value={element.system !== "UCR" && element.system !== 'ED WAIT'}
                                    id={element.id}
                                    onChange={(e) => handleChangeAnnouncement(e, "AGYLE")}
                                    name={"system"}
                                    label={t('For Agyle')}
                                />

                                {getConfig("modules.admin.edWaitAnnouncements") && (
                                    <AgyleSwitch
                                        value={element.system === "ED WAIT"}
                                        id={element.id}
                                        onChange={(e) => handleChangeAnnouncement(e, "ED WAIT")}
                                        name={"system"}
                                        label={t('For ED Wait Dashboard')}
                                    />
                                )}

                                <AgyleSwitch
                                    value={element.counter}
                                    id={element.id}
                                    onChange={handleChangeAnnouncement}
                                    name={"counter"}
                                    label={t('Add countdown')}
                                />
                            </Grid>

                            <Grid item xs={12} className={classes.divRow} style={{justifyContent: 'flex-end'}}>
                                <AgyleButton
                                    variant={"contained"}
                                    className={classes.button}
                                    color={"error"}
                                    onClick={() => {
                                        deleteID.current = element.id;
                                        openDeleteAnnouncement();
                                    }}
                                >
                                    {t("Delete")}
                                </AgyleButton>

                                <AgyleButton
                                    variant={"contained"}
                                    className={classes.button}
                                    onClick={handleSaveAnnouncements.bind(this, element)}
                                >
                                    {t("Save")}
                                </AgyleButton>
                            </Grid>
                        </Grid>
                    </Fragment>
                ))}
            </AgyleDialog>

            <AgyleDialog
                title={t("Add Announcement")}
                open={isAddAnnouncementOpen}
                onClose={closeAddAnnouncement}
                width={"sm"}
                fullWidth
                onSave={handleAddAnnouncement}
            >
                <FormLabel>
                    {t("Enter announcement text")}
                </FormLabel>

                <AgyleTextField
                    name={"value"}
                    label={t("Text")}
                    onChange={handleChangeNewAnnouncement}
                    value={announcementText}
                    variant="outlined"
                    multiline
                    fullWidth
                />
            </AgyleDialog>

            <MessageBox
                onClose={handleCloseDeleteAnnouncements}
                title={t("Confirmation")}
                haveAccept={{yes: t("Submit"), no: t("Close")}}
                onAccept={handleDeleteAnnouncement}
                message={t("Are you sure you want to delete this announcement?")}
                open={isDeleteAnnouncementOpen}
            />
        </>
    );
};

export default AnnouncementPanel;